import { create } from 'zustand';

export type Theme = 'light' | 'dark';

export interface Toast {
  id: string;
  kind: 'info' | 'success' | 'error';
  message: string;
}

interface UiState {
  theme: Theme;
  cheatSheetOpen: boolean;
  commandPaletteOpen: boolean;
  companySwitcherOpen: boolean;
  booksRevision: number;
  toasts: Toast[];

  setTheme(theme: Theme): void;
  toggleTheme(): void;
  setCheatSheetOpen(open: boolean): void;
  setCommandPaletteOpen(open: boolean): void;
  setCompanySwitcherOpen(open: boolean): void;
  refreshBooks(): void;
  pushToast(toast: Omit<Toast, 'id'>): void;
  dismissToast(id: string): void;
}

const STORAGE_KEY = 'khata.theme';

function initialTheme(): Theme {
  if (typeof window === 'undefined') return 'light';
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored === 'light' || stored === 'dark') return stored;
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function applyTheme(theme: Theme) {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute('data-theme', theme);
  window.localStorage.setItem(STORAGE_KEY, theme);
}

export const useUiStore = create<UiState>((set, get) => ({
  theme: initialTheme(),
  cheatSheetOpen: false,
  commandPaletteOpen: false,
  companySwitcherOpen: false,
  booksRevision: 0,
  toasts: [],

  setTheme(theme) {
    applyTheme(theme);
    set({ theme });
  },

  toggleTheme() {
    const next = get().theme === 'dark' ? 'light' : 'dark';
    applyTheme(next);
    set({ theme: next });
  },

  setCheatSheetOpen(open) {
    set({ cheatSheetOpen: open });
  },

  setCommandPaletteOpen(open) {
    set({ commandPaletteOpen: open });
  },

  setCompanySwitcherOpen(open) {
    set({ companySwitcherOpen: open });
  },

  refreshBooks() {
    set((state) => ({ booksRevision: state.booksRevision + 1 }));
  },

  pushToast(toast) {
    const id = crypto.randomUUID();
    set((state) => ({ toasts: [...state.toasts, { ...toast, id }] }));
    if (toast.kind !== 'error') {
      setTimeout(() => get().dismissToast(id), 3500);
    }
  },

  dismissToast(id) {
    set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) }));
  },
}));

applyTheme(initialTheme());
